import React, {
  Fragment,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import {
  checksheetCopy,
  checksheetGetAllByYear,
  checksheetGetAllProposedByYear,
  checksheetSave,
} from 'apis/checksheetAPIs';
import { setTemplateList } from 'state/slices/dataListsSlice';
import { Button } from '@asu/components-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBuildingColumns,
  faCalendar,
  faLocationDot,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import StaticData from './StaticData';
import { classNames } from 'utils';
import useServiceAuth from 'auth/useServiceAuth';
import { templateGetAllByYear } from 'apis/templateAPIs';

const programTypes = {
  UGRD: 'undergrad',
  MIN: 'minor',
  CERT: 'cert',
  // IDS: 'ids',
};

const proposedString = '(proposed) ';

const SearchProposed = ({ proposed, college }) => {
  return (
    <>
      <div className="d-flex gap-2 justify-content-between">
        <div className="fw-bold" style={{ fontSize: '18px' }}>
          (PROPOSED) {proposed.checksheetObj.checksheetName}
        </div>
        <div style={{ marginTop: '3px' }}>
          {proposed.checksheetObj.acadplan}
        </div>
      </div>
      {!!college?.description && (
        <div className="d-flex gap-1 align-items-center">
          <FontAwesomeIcon
            icon={faBuildingColumns}
            className="text-gray-6"
            width={'16px'}
          />
          {college.description}
        </div>
      )}
    </>
  );
};

const SearchProgram = ({ program }) => {
  const { degree, owners, campusesOffered } = useMemo(() => {
    const degree = program.degreeDescriptionShort
      ? program.degreeDescriptionShort === 'CERT'
        ? ', Certificate'
        : `, ${program.degreeDescriptionShort}`
      : program.acadPlanType === 'MIN'
        ? ', Minor'
        : null;

    // Use Set for O(1) deduplication
    const ownerSet = new Set();
    const owners = [];
    program.owners.forEach((programOwner) => {
      if (!ownerSet.has(programOwner.collegeAcadOrg)) {
        ownerSet.add(programOwner.collegeAcadOrg);
        owners.push({
          collegeAcadOrg: programOwner.collegeAcadOrg,
          collegeDescription: programOwner.collegeDescription,
        });
      }
    });

    const campusesOffered = [];
    if (program.campusesOffered && program.campusesOffered.length > 0) {
      program.campusesOffered.forEach((campus) => {
        const obj = { ...campus };
        const campusFound = StaticData.campusUndergradList.find(
          (campusUndergrad) => campusUndergrad.value === campus.campusCode
        );
        if (campusFound?.label) obj.label = campusFound.label;
        campusesOffered.push(obj);
      });
    }

    return { degree, owners, campusesOffered };
  }, [program]);

  return (
    <>
      <div className="d-flex gap-2 justify-content-between">
        <div className="fw-bold" style={{ fontSize: '18px' }}>
          {program.acadPlanDescription}
          {degree}
        </div>
        <div style={{ marginTop: '3px' }}>{program.acadPlanCode}</div>
      </div>
      {program.subplan && (
        // program.acadPlanCode !== 'LSBISBIS' &&
        <div className="d-flex gap-2 justify-content-between">
          <div className="fw-bold">
            {program.subplan.description}
            <span className="subplan-tag ms-1">Subplan</span>
          </div>
          <div style={{ marginTop: '3px' }}>
            {program.subplan.acadSubPlanCode}
          </div>
        </div>
      )}
      <div className="d-flex gap-1 align-items-center">
        <FontAwesomeIcon
          icon={faBuildingColumns}
          className="text-gray-6"
          width={'16px'}
        />
        {owners.map((owner, index, array) => (
          <Fragment key={owner.collegeAcadOrg}>
            {owner.collegeDescription}
            {index !== array.length - 1 && ', '}
          </Fragment>
        ))}
      </div>
      <div className="d-flex gap-1 align-items-center">
        <FontAwesomeIcon
          icon={faLocationDot}
          className="text-gray-6"
          width={'16px'}
        />
        {campusesOffered.map((campus, index, array) => (
          <Fragment key={campus.campusCode}>
            {campus.label}
            {index !== array.length - 1 && ', '}
          </Fragment>
        ))}
      </div>
    </>
  );
};

const NewProposedProgramForm = ({
  programList,
  collegeList,
  handleClose,
  user,
  year,
  refetchChecksheets,
}) => {
  const { getAccessToken } = useServiceAuth();
  const dispatch = useDispatch();

  const userCollege = user.college !== 'undefined' ? user.college : null;
  const userDepartments =
    user.department !== 'undefined' ? JSON.parse(user.department) : [];
  const yearObject = StaticData.yearList.reduce((acc, curr) => {
    acc[curr.value] = curr.label;
    return acc;
  }, {});

  const [searchString, setSearchString] = useState('');
  const [college, setCollege] = useState(userCollege);
  const [department, setDepartment] = useState(
    userDepartments.length === 1 ? userDepartments[0] : null
  );
  const [programName, setProgramName] = useState('');
  const [programType, setProgramType] = useState(null);
  const [catalogYear, setCatalogYear] = useState(year);
  const [copyFromCatalogYear, setCopyFromCatalogYear] = useState(null);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedProgramDegree, setSelectedProgramDegree] = useState(null);
  const [selectedRadio, setSelectedRadio] = useState(null);
  const [selectedChecksheet, setSelectedChecksheet] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedTemplateName, setSelectedTemplateName] = useState('');
  const [checksheetList, setChecksheetList] = useState([]);
  const [proposedList, setProposedList] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [error, setError] = useState(null);

  const templateList = useSelector((state) => state.dataLists.templateList);
  const departmentList = useSelector((state) => state.dataLists.departmentList);
  // const idsConcentrations = useSelector(
  //   (state) => state.dataLists.idsConcentrationList
  // );

  // Create lookup maps for O(1) access
  const checksheetLookup = useMemo(() => {
    const lookup = new Map();
    checksheetList.forEach((checksheet) => {
      const [, , planCode, subPlanCode] = checksheet.id.split('*');
      const key = `${planCode}:${subPlanCode}`;
      lookup.set(key, checksheet);
    });
    return lookup;
  }, [checksheetList]);

  // Optimize idsChecksheets computation
  // const idsChecksheets = useMemo(() => {
  //   const checksheets = {};
  //   const checksheetMap = new Map();

  //   // Build lookup map first
  //   checksheetList.forEach((cs) => {
  //     const [, college, planCode, subPlanCode] = cs.id.split('*');
  //     checksheetMap.set(`${college}:${planCode}:${subPlanCode}`, cs);
  //   });

  //   // Single pass through concentrations
  //   idsConcentrations
  //     .filter((conc) => conc.owner)
  //     .forEach((conc) => {
  //       const key = `${conc.owner.college}:LSBISBIS:${conc.subplan.acadSubPlanCode}`;
  //       const checksheet = checksheetMap.get(key);
  //       if (checksheet) {
  //         checksheets[conc.subplan.acadSubPlanCode] = checksheet;
  //       }
  //     });

  //   return checksheets;
  // }, [checksheetList, idsConcentrations]);

  // Optimize search filtering with single pass
  const filteredResults = useMemo(() => {
    if (!searchString)
      return {
        proposed: [],
        programs: [],
        // ids: []
      };

    const searchLower = searchString.toLowerCase();

    // Filter proposed
    const proposed = proposedList
      .filter((proposed) =>
        programType === 'UGRD'
          ? proposed.programType !== 'MIN' && proposed.programType !== 'CERT'
          : programType === 'MIN'
            ? proposed.programType === 'MIN'
            : programType === 'CERT'
              ? proposed.programType === 'CERT'
              : // : programType === 'IDS'
                //   ? proposed.programType === 'IDS'
                true
      )
      .filter(
        (proposed) =>
          (!!selectedTemplate &&
            proposed.templateId.split('*')[1] ===
              selectedTemplate.split('*')[1]) ||
          !selectedTemplate
      )
      .filter(
        (proposed) =>
          proposed.checksheetObj.acadplan.toLowerCase().includes(searchLower) ||
          (proposedString + proposed.checksheetObj.checksheetName)
            .toLowerCase()
            .includes(searchLower)
      );

    // Filter programs using checksheetLookup for O(1) access
    const programs = programList
      .filter((program) =>
        programType === 'UGRD'
          ? program.acadPlanType !== 'MIN' &&
            program.acadPlanType !== 'CER' &&
            program.acadPlanType !== 'SAC'
          : programType === 'MIN'
            ? program.acadPlanType === 'MIN'
            : programType === 'CERT'
              ? program.acadPlanType === 'CER' || program.acadPlanType === 'SAC'
              : // : programType === 'IDS'
                //   ? false // Regular programs don't match IDS type
                true
      )
      .filter((program) => {
        const key = `${program.acadPlanCode}:${program.subplan?.acadSubPlanCode || 'null'}`;
        const checksheet = checksheetLookup.get(key);

        if (!checksheet) return false;

        return (
          !selectedTemplate ||
          checksheet.templateId.split('*')[1] === selectedTemplate.split('*')[1]
        );
      })
      .filter(
        (program) =>
          program.acadPlanCode.toLowerCase().includes(searchLower) ||
          program.acadPlanDescription.toLowerCase().includes(searchLower) ||
          (program.subplan &&
            (program.subplan.acadSubPlanCode
              .toLowerCase()
              .includes(searchLower) ||
              program.subplan.description.toLowerCase().includes(searchLower)))
      );

    // Filter IDS concentrations with checksheets
    // const ids = idsConcentrations.filter((conc) => {
    //   if (!conc.owner || !idsChecksheets[conc.subplan.acadSubPlanCode])
    //     return false;

    //   // Filter by program type - only show for IDS type
    //   if (programType && programType !== 'IDS') return false;

    //   // Filter by template
    //   const checksheet = idsChecksheets[conc.subplan.acadSubPlanCode];
    //   if (
    //     selectedTemplate &&
    //     checksheet.templateId.split('*')[1] !== selectedTemplate.split('*')[1]
    //   )
    //     return false;

    //   // Filter by search string
    //   return (
    //     conc.acadPlanCode.toLowerCase().includes(searchLower) ||
    //     conc.acadPlanDescription.toLowerCase().includes(searchLower) ||
    //     conc.subplan.acadSubPlanCode.toLowerCase().includes(searchLower)
    //   );
    // });

    return {
      proposed,
      programs,
      // ids
    };
  }, [
    searchString,
    proposedList,
    programType,
    selectedTemplate,
    programList,
    checksheetLookup,
    // idsConcentrations,
    // idsChecksheets,
  ]);

  const { data: templateData, isSuccess } = useQuery({
    queryKey: ['templates', catalogYear],
    queryFn: async () => {
      const token = await getAccessToken();
      return templateGetAllByYear({ token, year: catalogYear });
    },
    enabled: !!catalogYear,
  });

  const {
    data: checksheetData,
    error: getChecksheetError,
    isError: getChecksheetIsError,
    isSuccess: getChecksheetIsSuccess,
  } = useQuery({
    queryKey: ['checksheets', copyFromCatalogYear],
    queryFn: async () => {
      const token = await getAccessToken();
      return checksheetGetAllByYear({ year: copyFromCatalogYear, token });
    },
    enabled: !!copyFromCatalogYear,
  });

  const {
    data: proposedData,
    error: getProposedError,
    isError: getProposedIsError,
    isSuccess: getProposedIsSuccess,
  } = useQuery({
    queryKey: ['proposed', copyFromCatalogYear],
    queryFn: async () => {
      const token = await getAccessToken();
      return checksheetGetAllProposedByYear({
        year: copyFromCatalogYear,
        token,
      });
    },
    enabled: !!copyFromCatalogYear,
  });

  const { mutate: saveChecksheet } = useMutation({
    mutationFn: checksheetSave,
    onSuccess: async (data, { token }) => {
      refetchChecksheets();
    },
  });

  const { mutate: copyChecksheet } = useMutation({
    mutationFn: checksheetCopy,
    onSuccess: async (data, { token }) => {
      refetchChecksheets();
    },
  });

  const handleCollegeChange = (e) => {
    const { value } = e.target;
    setCollege(value);
    setDepartment(null);
  };

  const handleSetProgramType = (e) => {
    const { value } = e.target;
    setProgramType(value);

    if (
      selectedRadio === 'existingChecksheet' &&
      selectedChecksheet &&
      ((value === 'MIN' && selectedChecksheet.programType !== 'MIN') ||
        (value === 'CERT' && selectedChecksheet.programType !== 'CERT') ||
        // (value === 'IDS' && selectedChecksheet.programType !== 'IDS') ||
        (value === 'UGRD' &&
          (selectedChecksheet.programType === 'MIN' ||
            selectedChecksheet.programType === 'CERT' ||
            // selectedChecksheet.programType === 'IDS' ||
            !selectedChecksheet.programType)))
    )
      setSelectedChecksheet(null);

    if (value === 'UGRD') {
      setSelectedTemplate(null);
      setSelectedTemplateName('');
    } else {
      const allMinorTemplates = templateList.filter(
        (template) => template.details.programType === 'minor'
      );
      const allCertificateTemplates = templateList.filter(
        (template) => template.details.programType === 'cert'
      );
      // const allIdsTemplates = templateList.filter(
      //   (template) => template.details.programType === 'ids'
      // );

      if (value === 'MIN' && allMinorTemplates.length === 1) {
        setSelectedTemplate(allMinorTemplates[0].id);
        setSelectedTemplateName(allMinorTemplates[0].details.templateName);
      } else if (value === 'CERT' && allCertificateTemplates.length === 1) {
        setSelectedTemplate(allCertificateTemplates[0].id);
        setSelectedTemplateName(
          allCertificateTemplates[0].details.templateName
        );
        // } else if (value === 'IDS' && allIdsTemplates.length === 1) {
        //   setSelectedTemplate(allIdsTemplates[0].id);
        //   setSelectedTemplateName(allIdsTemplates[0].details.templateName);
      } else {
        setSelectedTemplate(null);
        setSelectedTemplateName('');
      }
    }
  };

  const handleCreateChecksheet = async () => {
    if (selectedRadio === 'blankChecksheet') {
      const jsonData = {
        year: catalogYear,
        templateId: selectedTemplate,
        checksheetName: programName,
        programType: programType,
        acadplan: null,
        acadsubplan: null,
        college: college,
        department: department,
        componentRequirements: {},
        courseLists: {},
        proposed: true,
      };

      const token = await getAccessToken();
      await saveChecksheet({
        jsonData: jsonData,
        token: token,
      });

      handleClose();
    } else if (selectedChecksheet) {
      const jsonData = {
        checksheetfrom: selectedChecksheet.id,
        acadplanto: null,
        acadsubplanto: null,
        collegeto: college,
        departmentto: department,
        nameto: programName,
        programtypeto: programType,
        yearto: catalogYear,
        proposed: true,
      };

      const token = await getAccessToken();
      copyChecksheet({ jsonData: jsonData, token: token });

      handleClose();
    } else {
      alert('Program not selected');
    }
  };

  const handleSelectTemplate = (e) => {
    const { value, selectedOptions } = e.target;

    if (
      !!selectedChecksheet &&
      value.split('*')[1] !== selectedChecksheet.templateId.split('*')[1]
    )
      setSelectedChecksheet(null);

    setSelectedTemplate(value);
    setSelectedTemplateName(selectedOptions[0].text);
  };

  const handleSelectRadio = (e) => {
    setSelectedRadio(e.target.value);
    setSelectedChecksheet(null);
  };

  const handleSelectChecksheet = useCallback(
    (program, proposed = false) => {
      let cs;

      if (!proposed) {
        const key = `${program.acadPlanCode}:${program.subplan?.acadSubPlanCode || 'null'}`;
        cs = checksheetLookup.get(key);
        setSelectedProgram(program);
      } else {
        cs = program;
        setSelectedProgram({
          acadPlanCode: program.checksheetObj.acadplan,
          acadPlanDescription: program.checksheetObj.checksheetName,
          college: program.checksheetObj.college,
          proposed: true,
        });
      }

      setSelectedChecksheet(cs);
      setSearchString('');

      if (cs) {
        const foundTemplate = templateList.find(
          (template) =>
            template.id.split('*')[1] === cs.templateId.split('*')[1]
        );

        if (foundTemplate) {
          setSelectedTemplate(foundTemplate.id);
          setSelectedTemplateName(foundTemplate.details.templateName);
        } else {
          setSelectedTemplate(null);
          setSelectedTemplateName('');
        }

        if (cs.programType === 'MIN') setProgramType('MIN');
        else if (cs.programType === 'CERT') setProgramType('CERT');
        // else if (cs.programType === 'IDS') setProgramType('IDS');
        else if (!!cs.programType) setProgramType('UGRD');
        else setProgramType(null);
      } else setSelectedTemplate(null);
    },
    [checksheetLookup, templateList]
  );

  // const handleSelectIdsChecksheet = useCallback(
  //   (concentration, checksheet) => {
  //     setSelectedProgram(concentration);
  //     setSelectedChecksheet(checksheet);
  //     setSearchString('');

  //     if (checksheet) {
  //       const foundTemplate = templateList.find(
  //         (template) =>
  //           template.id.split('*')[1] === checksheet.templateId.split('*')[1]
  //       );

  //       if (foundTemplate) {
  //         setSelectedTemplate(foundTemplate.id);
  //         setSelectedTemplateName(foundTemplate.details.templateName);
  //       } else {
  //         setSelectedTemplate(null);
  //         setSelectedTemplateName('');
  //       }

  //       setProgramType('IDS');
  //     }
  //   },
  //   [templateList]
  // );

  const handleClearSelectedChecksheet = () => {
    setSearchString('');
    setSelectedChecksheet(null);
  };

  useEffect(() => {
    if (getChecksheetIsError) setError(getChecksheetError.message);

    if (getChecksheetIsSuccess) {
      const next = [];

      checksheetData.forEach((checksheet) => {
        const idYear = checksheet['sk'].S.split('*')[0];
        let lockedBy = {};

        if (!!checksheet['lockedBy']?.S) {
          lockedBy = JSON.parse(checksheet['lockedBy'].S);
        }

        const obj = {
          id: checksheet['sk'].S,
          programType: checksheet['programType'].S,
          checksheetObj: checksheet,
          catalogYear: idYear,
          status: checksheet['progress']?.S ?? null,
          updatedBy: checksheet['updatedBy']?.S,
          updatedOn: checksheet['updatedOn']?.S,
          templateId: checksheet['templateId']?.S ?? null,
          lockedBy: lockedBy,
          proposed: checksheet['proposed'] ? true : false,
        };

        next.push(obj);
      });

      setChecksheetList([...next]);
    }
  }, [
    checksheetData,
    getChecksheetError,
    getChecksheetIsError,
    getChecksheetIsSuccess,
  ]);

  useEffect(() => {
    if (getProposedIsError) setError(getProposedError.message);

    if (getProposedIsSuccess) {
      const next = [];

      proposedData.forEach((checksheet) => {
        const idYear = checksheet['sk'].S.split('*')[0];
        let lockedBy = {};

        if (!!checksheet['lockedBy']?.S) {
          lockedBy = JSON.parse(checksheet['lockedBy'].S);
        }

        const obj = {
          id: checksheet['sk'].S,
          programType: checksheet['programType'].S,
          checksheetObj: checksheet,
          catalogYear: idYear,
          status: checksheet['progress']?.S ?? null,
          updatedBy: checksheet['updatedBy']?.S,
          updatedOn: checksheet['updatedOn']?.S,
          templateId: checksheet['templateId']?.S ?? null,
          lockedBy: lockedBy,
          checksheetName: checksheet['checksheetName'],
          proposed: checksheet['proposed'] ? true : false,
        };

        next.push(obj);
      });

      setProposedList([...next]);
    }
  }, [
    proposedData,
    getProposedError,
    getProposedIsError,
    getProposedIsSuccess,
  ]);

  useEffect(() => {
    if (isSuccess) {
      const next = [];
      templateData.forEach((template) => {
        if (template['details']) {
          const jsonObject = JSON.parse(template['details'].S);
          const obj = {
            id: template['sk'].S,
            details: jsonObject,
            templateObj: template,
          };
          next.push(obj);
        }
      });

      dispatch(setTemplateList(next));
    }
  }, [templateData, dispatch, isSuccess]);

  useEffect(() => {
    if (selectedProgram) {
      const dds = selectedProgram.degreeDescriptionShort
        ? selectedProgram.degreeDescriptionShort === 'CERT'
          ? ', Certificate'
          : `, ${selectedProgram.degreeDescriptionShort}`
        : selectedProgram.acadPlanType === 'MIN'
          ? ', Minor'
          : null;

      setSelectedProgramDegree(dds);
    }
  }, [selectedProgram]);

  useEffect(() => {
    let filtered = templateList.filter(
      (template) =>
        (!!programType &&
          template.details.programType === programTypes[programType]) ||
        !programType
    );

    setFilteredTemplates(filtered);
  }, [programType, selectedChecksheet, templateList]);

  if (error) return <div>Error: {error}</div>;

  return (
    <div className="p-6" style={{ width: '1100px' }}>
      <div className="fs-1 fw-bold mb-4">New proposed program</div>
      <div className="mb-5">
        <div className="fw-bold">College/school</div>
        {!!userCollege ? (
          <div>{collegeList[userCollege].description}</div>
        ) : (
          <select
            style={{ width: '40%' }}
            value={college ?? ''}
            onChange={(e) => handleCollegeChange(e)}
          >
            <option value="">Select college or school</option>
            {Object.entries(collegeList)
              .filter(
                ([code, obj]) =>
                  code !== 'COE' &&
                  code !== 'CGC' &&
                  code !== 'CLW' &&
                  code !== 'CTPH' &&
                  code !== 'CPROVOST'
              )
              .toSorted(([aCode, aObj], [bCode, bObj]) =>
                aObj.alphaSortDescription.localeCompare(
                  bObj.alphaSortDescription
                )
              )
              .map(([code, obj]) => (
                <option key={code} value={code}>
                  {obj.alphaSortDescription}
                </option>
              ))}
            <option value="OTHER">Other</option>
          </select>
        )}
      </div>
      <div className="mb-5">
        <div className="fw-bold">Department</div>
        {userDepartments.length === 1 ? (
          <div>{departmentList[userDepartments[0]].description}</div>
        ) : (
          <select
            style={{ width: '40%' }}
            value={department ?? ''}
            onChange={(e) => setDepartment(e.target.value)}
            disabled={
              college === 'OTHER' ||
              (!!college &&
                !!collegeList[college] &&
                !collegeList[college].departments)
            }
          >
            <option value="">Select department</option>
            {!!college &&
              college !== 'OTHER' &&
              !!collegeList[college].departments &&
              Object.entries(collegeList[college].departments)
                .filter(([code, obj]) =>
                  !!userDepartments.length
                    ? userDepartments.includes(code)
                    : true
                )
                .toSorted(([aCode, aObj], [bCode, bObj]) =>
                  aObj.description.localeCompare(bObj.description)
                )
                .map(([code, obj]) => (
                  <option key={code} value={code}>
                    {obj.description}
                  </option>
                ))}
          </select>
        )}
      </div>
      <div className="mb-5">
        <div className="fw-bold">Catalog year</div>
        <select
          value={catalogYear ?? ''}
          onChange={(e) => setCatalogYear(e.target.value)}
          style={{ width: '40%' }}
        >
          <option value="">Select catalog year</option>
          {StaticData.yearList.map((year) => (
            <option key={year.value} value={year.value}>
              {year.label}
            </option>
          ))}
        </select>
      </div>
      <div className="mb-5">
        <div className="fw-bold">Program name</div>
        <input
          type="text"
          placeholder="Enter program name"
          style={{ width: '40%' }}
          value={programName}
          onChange={(e) => setProgramName(e.target.value)}
        />
      </div>
      <div className="mb-5">
        <div className="fw-bold">Program type</div>
        <select
          value={programType ?? ''}
          onChange={(e) => handleSetProgramType(e)}
          style={{ width: '40%' }}
        >
          <option value="">Select program type</option>
          <option value="UGRD">Undergraduate degree</option>
          <option value="MIN">Minor</option>
          <option value="CERT">Certificate</option>
          {/* <option value="IDS">IDS Concentration</option> */}
        </select>
      </div>
      <div className="mb-6" style={{ width: '70%' }}>
        <div className="fw-bold">Choose a starter template</div>
        <div className="mb-1">
          A template is required to set up the checksheet with essential
          requirements and validation rules for the appropriate program type.
        </div>
        {!!selectedChecksheet &&
          !filteredTemplates.some(
            (template) =>
              template.id.split('*')[1] ===
              selectedChecksheet.templateId.split('*')[1]
          ) && (
            <div className="text-maroon fw-bold" style={{ width: '50%' }}>
              *A matching template was not found
            </div>
          )}
        <select
          style={{ width: '40%' }}
          value={selectedTemplate ?? ''}
          onChange={(e) => handleSelectTemplate(e)}
        >
          <option value="">Select template</option>
          {filteredTemplates.map((template) => (
            <option key={template.id} value={template.id}>
              {template.details.templateName}
            </option>
          ))}
          {!filteredTemplates.length && (
            <option value="blank" disabled>
              No templates were found that match current selections
            </option>
          )}
        </select>
      </div>
      <form className="uds-form mb-2" style={{ width: '70%' }}>
        <div className="fw-bold">Choose a method to set up this checksheet</div>
        <fieldset>
          <div className="form-check">
            <input
              className="form-check-input"
              type="radio"
              id="blankChecksheet"
              data-ga-input="radio button"
              data-ga-input-name="onclick"
              data-ga-input-event="select"
              data-ga-input-action="click"
              data-ga-input-region="main content"
              data-ga-input-section="Create from blank checksheet"
              value="blankChecksheet"
              checked={selectedRadio === 'blankChecksheet'}
              onChange={(e) => handleSelectRadio(e)}
            />
            <label className="form-check-label" htmlFor="blankChecksheet">
              <div>Create from blank checksheet</div>
              <div className="fw-normal text-gray-6">
                Build program requirements from scratch
              </div>
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="radio"
              id="existingProgram"
              data-ga-input="radio button"
              data-ga-input-name="onclick"
              data-ga-input-event="select"
              data-ga-input-action="click"
              data-ga-input-region="main content"
              data-ga-input-section="Create from existing program"
              value="existingChecksheet"
              checked={selectedRadio === 'existingChecksheet'}
              onChange={(e) => handleSelectRadio(e)}
            />
            <label className="form-check-label" htmlFor="existingProgram">
              <div>Create from existing checksheet</div>
              <div className="fw-normal text-gray-6">
                Recommended for programs with similar requirements and shared
                properties: catalog year, program type and template.
              </div>
            </label>
          </div>
        </fieldset>
      </form>

      {selectedRadio === 'existingChecksheet' &&
        (!selectedChecksheet ? (
          <>
            <div className="mb-5">
              <div className="fw-bold">Copy from catalog year</div>
              <select
                value={copyFromCatalogYear ?? ''}
                onChange={(e) => setCopyFromCatalogYear(e.target.value)}
                style={{ width: '40%' }}
              >
                <option value="">Select catalog year to copy from</option>
                {StaticData.yearList.map((year) => (
                  <option key={year.value} value={year.value}>
                    {year.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="mb-9">
              <div className="fw-bold lh-1 mb-1">Search programs</div>
              <div className="mb-1">
                Note that you may only choose a program with matching criteria:
              </div>
              <div className="d-flex gap-1 mb-2">
                {copyFromCatalogYear && (
                  <div className="bg-gray-3 rounded-pill px-1">
                    {yearObject[copyFromCatalogYear]}
                  </div>
                )}
                {programType && (
                  <div className="bg-gray-3 rounded-pill px-1">
                    {programType === 'UGRD'
                      ? 'undergraduate degree'
                      : programType === 'MIN'
                        ? 'minor'
                        : programType === 'CERT'
                          ? 'certificate'
                          : // : programType === 'IDS'
                            //   ? 'IDS concentration'
                            null}
                    {((programType === 'UGRD' &&
                      selectedTemplateName.toLowerCase() === 'undergraduate') ||
                      (programType === 'MIN' &&
                        selectedTemplateName.toLowerCase() === 'minor') ||
                      (programType === 'CERT' &&
                        selectedTemplateName.toLowerCase() ===
                          'certificate')) &&
                      // || (programType === 'IDS' &&
                      // selectedTemplateName.toLowerCase() ===
                      //   'ids concentration')
                      ' (type)'}
                  </div>
                )}
                {!!selectedTemplate && !!selectedTemplateName && (
                  <div className="bg-gray-3 rounded-pill px-1">
                    {selectedTemplateName}
                    {((programType === 'UGRD' &&
                      selectedTemplateName.toLowerCase() === 'undergraduate') ||
                      (programType === 'MIN' &&
                        selectedTemplateName.toLowerCase() === 'minor') ||
                      (programType === 'CERT' &&
                        selectedTemplateName.toLowerCase() ===
                          'certificate')) &&
                      // || (programType === 'IDS' &&
                      // selectedTemplateName.toLowerCase() ===
                      //   'ids concentration')
                      ' (template)'}
                  </div>
                )}
              </div>
              <div className="position-relative">
                <div className="d-flex gap-2 align-items-center">
                  <input
                    placeholder="Search by program title or plan code"
                    value={searchString}
                    onChange={(e) => setSearchString(e.target.value)}
                    style={{ width: '40%' }}
                  />
                  {!copyFromCatalogYear && !!searchString && (
                    <div
                      className="text-maroon fw-bold"
                      style={{ width: '50%' }}
                    >
                      *Select a catalog year to copy from first to see results
                    </div>
                  )}
                </div>
                {searchString && (
                  <div
                    className="border border-gray-5 w-100 overflow-y-auto"
                    style={{ maxHeight: '400px', zIndex: 1 }}
                  >
                    {filteredResults.proposed.map((proposed, index, array) => (
                      <div key={index} className="bg-white px-2">
                        <div
                          className={classNames(
                            (index !== array.length - 1 ||
                              filteredResults.programs.length > 0) &&
                              // || filteredResults.ids.length > 0
                              'border-bottom',
                            'border-gray-5 py-2'
                          )}
                          onClick={() => handleSelectChecksheet(proposed, true)}
                          role="button"
                        >
                          <SearchProposed
                            proposed={proposed}
                            college={
                              proposed.checksheetObj.college !== 'OTHER'
                                ? collegeList[proposed.checksheetObj.college]
                                : { description: 'Other' }
                            }
                          />
                        </div>
                      </div>
                    ))}
                    {!!copyFromCatalogYear &&
                      filteredResults.programs.map((program, index, array) => (
                        <div key={index} className="bg-white px-2">
                          <div
                            className={classNames(
                              index !== array.length - 1 &&
                                // || filteredResults.ids.length > 0
                                'border-bottom',
                              'border-gray-5 py-2'
                            )}
                            onClick={() =>
                              handleSelectChecksheet(program, false)
                            }
                            role="button"
                          >
                            <SearchProgram program={program} />
                          </div>
                        </div>
                      ))}
                    {/* {filteredResults.ids.map((conc, index, array) => (
                      <div key={index} className="bg-white px-2">
                        <div
                          className={classNames(
                            index !== array.length - 1 && 'border-bottom',
                            'border-gray-5 py-2'
                          )}
                          onClick={() =>
                            handleSelectIdsChecksheet(
                              conc,
                              idsChecksheets[conc.subplan.acadSubPlanCode]
                            )
                          }
                          role="button"
                        >
                          <div className="d-flex gap-2 justify-content-between">
                            <div
                              className="fw-bold"
                              style={{ fontSize: '18px' }}
                            >
                              {conc.acadPlanDescription}
                            </div>
                            <div style={{ marginTop: '3px' }}>
                              {conc.subplan.acadSubPlanCode}
                            </div>
                          </div>
                          <div className="fw-bold mb-1">
                            <span className="subplan-tag">
                              Interdisciplinary studies
                            </span>
                          </div>
                          <div className="d-flex gap-1 align-items-center">
                            <FontAwesomeIcon
                              icon={faBuildingColumns}
                              className="text-gray-6"
                              width={'16px'}
                            />
                            {conc?.owner?.college &&
                              !!collegeList?.[conc.owner.college] &&
                              collegeList[conc.owner.college].description}
                          </div>
                        </div>
                      </div>
                    ))} */}
                    {(!copyFromCatalogYear ||
                      (!filteredResults.proposed.length &&
                        !filteredResults.programs.length)) && (
                      //  && !filteredResults.ids.length
                      <div className="p-2 fst-italic">No results found</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="bg-gray-1 border border-gray-3 w-100 mb-3">
              <div className="p-2">
                <div className="d-flex justify-content-between mb-1">
                  <div className="fw-bold" style={{ fontSize: '18px' }}>
                    {selectedProgram.acadPlanDescription}
                    {selectedProgramDegree}
                  </div>
                  <div className="d-flex gap-3 align-items-center">
                    <div>{selectedProgram.acadPlanCode}</div>
                    <div
                      className="bg-white border border-gray-3 lh-1 rounded-circle"
                      style={{
                        height: 'min-content',
                        padding: '3px 6px 5px',
                      }}
                      onClick={handleClearSelectedChecksheet}
                      role="button"
                    >
                      <FontAwesomeIcon
                        icon={faXmark}
                        style={{ height: '12px', width: '12px' }}
                      />
                    </div>
                  </div>
                </div>

                {selectedProgram.subplan && (
                  // selectedProgram.acadPlanCode !== 'LSBISBIS' &&
                  <div className="d-flex gap-2 justify-content-between">
                    <div className="fw-bold">
                      {selectedProgram.subplan.description}
                      <span className="subplan-tag ms-1">Subplan</span>
                    </div>
                    <div style={{ margin: '3px 50px 0 0' }}>
                      {selectedProgram.subplan.acadSubPlanCode}
                    </div>
                  </div>
                )}
                {/* {selectedProgram?.acadPlanCode === 'LSBISBIS' &&
                  selectedProgram?.subplan && (
                    <div className="fw-bold mb-1">
                      <span className="subplan-tag">
                        Interdisciplinary studies
                      </span>
                    </div>
                  )} */}
                <div className="d-flex gap-1 align-items-center mb-1">
                  <FontAwesomeIcon
                    icon={faBuildingColumns}
                    className="text-gray-5"
                    width={'16px'}
                  />
                  {!!selectedProgram?.college ? (
                    <div>
                      {selectedProgram.college !== 'OTHER'
                        ? collegeList[selectedProgram.college].description
                        : 'Other'}
                    </div>
                  ) : selectedProgram.owner ? (
                    <div>
                      {collegeList[selectedProgram.owner.college]?.description}
                    </div>
                  ) : (
                    !!selectedProgram.owners &&
                    selectedProgram.owners.map((owner, index, array) => (
                      <Fragment key={owner.collegeAcadOrg}>
                        {owner.collegeDescription}
                        {index !== array.length - 1 && ', '}
                      </Fragment>
                    ))
                  )}
                </div>
                {!selectedProgram.proposed && !selectedProgram.owner && (
                  <div className="d-flex gap-1 align-items-center mb-1">
                    <FontAwesomeIcon
                      icon={faLocationDot}
                      className="text-gray-5"
                      width={'16px'}
                    />
                    {!!selectedProgram.campusesOffered &&
                      selectedProgram.campusesOffered.map(
                        (campus, index, array) => (
                          <Fragment key={campus.campusCode}>
                            {
                              StaticData.campusUndergradList.find(
                                (camp) => camp.value === campus.campusCode
                              ).label
                            }
                            {index !== array.length - 1 && ', '}
                          </Fragment>
                        )
                      )}
                  </div>
                )}
                <div className="d-flex gap-1 align-items-center">
                  <FontAwesomeIcon
                    icon={faCalendar}
                    className="text-gray-5"
                    width={'16px'}
                  />
                  {yearObject[copyFromCatalogYear]}
                </div>
              </div>
            </div>
          </>
        ))}

      <div className="d-flex justify-content-between align-items-center">
        <Button
          color="dark"
          label="Cancel"
          size="medium"
          onClick={handleClose}
        />
        <Button
          color="gold"
          label="Create checksheet"
          size="medium"
          onClick={handleCreateChecksheet}
          disabled={
            !college ||
            (!department &&
              college !== 'OTHER' &&
              !!college &&
              !!collegeList[college] &&
              collegeList[college].departments) ||
            !programType ||
            !programName ||
            !catalogYear ||
            !selectedTemplate ||
            !selectedRadio ||
            (selectedRadio === 'exisitingChecksheet' && !selectedChecksheet)
          }
        />
      </div>
    </div>
  );
};

export default NewProposedProgramForm;
